import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Download, Plus, Trash2, ArrowLeft, Sparkles, RotateCcw, Building2 , Save, Clock , FileText, FileUp, FileDown} from "lucide-react";
import { useAuth, useSettings } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EscalationMatrixForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: "",
    date: new Date().toISOString().split("T")[0],
    prepared_by: "",
    escalation_levels: [{ level: 1, title: "", description: "", response_time: "", approver: "" }],
    escalation_procedures: [{ issue_type: "", procedure: "" }],
    contact_list: [{ name: "", role: "", email: "", phone: "", escalation_level: 1 }]
  });

  const getSampleData = () => ({
    project_name: "Enterprise CRM Implementation",
    date: new Date().toISOString().split("T")[0],
    prepared_by: "Project Management Office",
    escalation_levels: [
      { level: 1, title: "Team Level", description: "Issues resolved within the project team", response_time: "4 hours", approver: "Team Lead" },
      { level: 2, title: "Project Manager", description: "Issues requiring PM decision or resources", response_time: "8 hours", approver: "Project Manager" },
      { level: 3, title: "Steering Committee", description: "Major blockers, budget, or timeline changes", response_time: "24 hours", approver: "Steering Committee" },
      { level: 4, title: "Executive Sponsor", description: "Critical issues affecting business", response_time: "48 hours", approver: "VP/C-Level" }
    ],
    escalation_procedures: [
      { issue_type: "Technical Blocker", procedure: "1. Document issue and impact. 2. Attempt resolution with dev team. 3. Escalate to Tech Lead if unresolved in 4 hours. 4. Escalate to PM if critical." },
      { issue_type: "Resource Conflict", procedure: "1. Identify resource requirements. 2. Discuss with team lead. 3. Escalate to PM for arbitration. 4. Steering Committee for major conflicts." },
      { issue_type: "Scope Change", procedure: "1. Document change request. 2. Impact analysis. 3. PM review. 4. Steering Committee approval for major changes." },
      { issue_type: "Budget Overrun", procedure: "1. Document variance. 2. PM review and options. 3. Steering Committee approval required. 4. Executive sponsor for >10% overrun." }
    ],
    contact_list: [
      { name: "John Smith", role: "Tech Lead", email: "john.smith@company.com", phone: "+1-555-0101", escalation_level: 1 },
      { name: "Sarah Johnson", role: "Project Manager", email: "sarah.johnson@company.com", phone: "+1-555-0102", escalation_level: 2 },
      { name: "Mike Chen", role: "IT Director", email: "mike.chen@company.com", phone: "+1-555-0103", escalation_level: 3 },
      { name: "Lisa Wang", role: "VP Engineering", email: "lisa.wang@company.com", phone: "+1-555-0104", escalation_level: 4 }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Escalation Matrix Draft";
      const payload = {
        name: draftName,
        template_type: "Escalation Matrix",
        template_id: "escalation-matrix",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "escalation-matrix", "Escalation Matrix", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("escalation-matrix", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      let url = `${API}/generate/escalation-matrix`;
      if (selectedSetting && selectedSetting !== "none") {
        url += `?settings_id=${selectedSetting}`;
      }
      const response = await axios.post(url, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Escalation_Matrix_${formData.project_name || "Document"}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Escalation Matrix",
        formData,
        settings: activeSettings || {},
        templateType: "Escalation Matrix",
        templateId: "generate",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const updateLevel = (index, field, value) => {
    const updated = [...formData.escalation_levels];
    updated[index][field] = field === "level" ? parseInt(value) || 0 : value;
    setFormData({ ...formData, escalation_levels: updated });
  };

  const addLevel = () => {
    const nextLevel = formData.escalation_levels.length + 1;
    setFormData({
      ...formData,
      escalation_levels: [...formData.escalation_levels, { level: nextLevel, title: "", description: "", response_time: "", approver: "" }]
    });
  };

  const removeLevel = (index) => {
    setFormData({ ...formData, escalation_levels: formData.escalation_levels.filter((_, i) => i !== index) });
  };

  const updateProcedure = (index, field, value) => {
    const updated = [...formData.escalation_procedures];
    updated[index][field] = value;
    setFormData({ ...formData, escalation_procedures: updated });
  };

  const addProcedure = () => {
    setFormData({
      ...formData,
      escalation_procedures: [...formData.escalation_procedures, { issue_type: "", procedure: "" }]
    });
  };

  const removeProcedure = (index) => {
    setFormData({ ...formData, escalation_procedures: formData.escalation_procedures.filter((_, i) => i !== index) });
  };

  const updateContact = (index, field, value) => {
    const updated = [...formData.contact_list];
    updated[index][field] = field === "escalation_level" ? parseInt(value) || 0 : value;
    setFormData({ ...formData, contact_list: updated });
  };

  const addContact = () => {
    setFormData({
      ...formData,
      contact_list: [...formData.contact_list, { name: "", role: "", email: "", phone: "", escalation_level: 1 }]
    });
  };

  const removeContact = (index) => {
    setFormData({ ...formData, contact_list: formData.contact_list.filter((_, i) => i !== index) });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Escalation Matrix</h1>
                <p className="text-sm text-gray-500">Define escalation levels and procedures</p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Header/Footer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Document Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} placeholder="Project name" data-testid="project-name-input" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
              </div>
              <div>
                <Label>Prepared By</Label>
                <Input value={formData.prepared_by} onChange={(e) => setFormData({ ...formData, prepared_by: e.target.value })} placeholder="PMO" />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Escalation Levels</Label>
              {formData.escalation_levels.map((level, index) => (
                <Card key={index} className="p-4 bg-gray-50">
                  <div className="grid grid-cols-5 gap-4">
                    <div>
                      <Label>Level</Label>
                      <Input type="number" value={level.level} onChange={(e) => updateLevel(index, "level", e.target.value)} />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input value={level.title} onChange={(e) => updateLevel(index, "title", e.target.value)} placeholder="Team Level" />
                    </div>
                    <div>
                      <Label>Response Time</Label>
                      <Input value={level.response_time} onChange={(e) => updateLevel(index, "response_time", e.target.value)} placeholder="4 hours" />
                    </div>
                    <div>
                      <Label>Approver</Label>
                      <Input value={level.approver} onChange={(e) => updateLevel(index, "approver", e.target.value)} placeholder="Team Lead" />
                    </div>
                    <div className="flex items-end">
                      <Button variant="ghost" size="icon" onClick={() => removeLevel(index)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <Label>Description</Label>
                    <Input value={level.description} onChange={(e) => updateLevel(index, "description", e.target.value)} placeholder="Issues resolved at this level" />
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addLevel}>
                <Plus className="h-4 w-4 mr-2" /> Add Level
              </Button>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Escalation Procedures</Label>
              {formData.escalation_procedures.map((proc, index) => (
                <Card key={index} className="p-4 bg-gray-50">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Issue Type</Label>
                      <Input value={proc.issue_type} onChange={(e) => updateProcedure(index, "issue_type", e.target.value)} placeholder="Technical Blocker" />
                    </div>
                    <div className="col-span-2 flex gap-2">
                      <div className="flex-1">
                        <Label>Procedure</Label>
                        <Textarea value={proc.procedure} onChange={(e) => updateProcedure(index, "procedure", e.target.value)} placeholder="Step-by-step procedure" rows={2} />
                      </div>
                      <div className="flex items-end">
                        <Button variant="ghost" size="icon" onClick={() => removeProcedure(index)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addProcedure}>
                <Plus className="h-4 w-4 mr-2" /> Add Procedure
              </Button>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Contact List</Label>
              {formData.contact_list.map((contact, index) => (
                <Card key={index} className="p-4 bg-gray-50">
                  <div className="grid grid-cols-6 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input value={contact.name} onChange={(e) => updateContact(index, "name", e.target.value)} placeholder="John Smith" />
                    </div>
                    <div>
                      <Label>Role</Label>
                      <Input value={contact.role} onChange={(e) => updateContact(index, "role", e.target.value)} placeholder="Tech Lead" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={contact.email} onChange={(e) => updateContact(index, "email", e.target.value)} placeholder="email@company.com" />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input value={contact.phone} onChange={(e) => updateContact(index, "phone", e.target.value)} placeholder="+1-555-0101" />
                    </div>
                    <div>
                      <Label>Level</Label>
                      <Input type="number" value={contact.escalation_level} onChange={(e) => updateContact(index, "escalation_level", e.target.value)} />
                    </div>
                    <div className="flex items-end">
                      <Button variant="ghost" size="icon" onClick={() => removeContact(index)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addContact}>
                <Plus className="h-4 w-4 mr-2" /> Add Contact
              </Button>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
              <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EscalationMatrixForm;
